import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { IMaskMixin } from 'react-imask';
import { Formik, Form, Field, useField } from 'formik';
import { setFooterConfig } from '@app/actions/footerActions';
import LocalizedText from '@LocalizedText';

import AutoValidateForm from '@components/auto-validate/AutoValidateForm';
import EditableSelect from '@components/editable-select/EditableSelect';
import './RequestCodesForm.scss';

const MaskedInput = IMaskMixin(({ inputRef, field, ...props }) => {
  const [formikField] = useField(field);
  return <input {...props} {...formikField} ref={inputRef} />;
});

const MIN_CODES_NUMBER = 0;
const MAX_CODES_NUMBER = 2000000;

const codesNumberTooSmall = codesNumber => Number(codesNumber) > MIN_CODES_NUMBER;
const codesNumberTooBig = codesNumber => Number(codesNumber) <= MAX_CODES_NUMBER;

const validateCodesNumber = codesNumber => !(codesNumberTooSmall(codesNumber) && codesNumberTooBig(codesNumber));
const validateText = text => !text?.trim();

const RequestCodesForm = ({ brandIdList, setFormData, setFormValid, setFooterConfig }) => {
  const { current: hasSingleBrandId } = useRef(brandIdList.length === 1);

  const onFormValidated = (errors, values) => {
    setFormValid(!Object.keys(errors).length);
    setFormData(values);
  };

  return (
    <div id="request-codes-form">
      <Formik
        initialValues={{
          manuf_lot: '',
          manuf_name: '',
          manuf_city: '',
          manuf_state: '',
          manuf_date: null,
          manuf_country: '',
          manuf_country_other: '',
          client_req_ref: '',
          codes_number: '',
          brand_id: '',
          gtin: '',
          sku: '',
        }}
      >
        {({ errors, touched, values, setFieldValue }) => (
          <Form autoComplete="off">
            <div className="form-column left">
              <label htmlFor="request-codes-gtin" className="required">
                <LocalizedText id="qau-request-codes-gtin" tagName="span" />
                <Field
                  type="text"
                  name="gtin"
                  id="request-codes-gtin"
                  validate={validateText}
                  className={classnames({ invalid: errors.gtin && touched.gtin })}
                  maxLength="64"
                />
              </label>

              <label htmlFor="request-codes-number" className="required">
                <LocalizedText id="qau-request-codes-number" tagName="span" />
                <Field
                  type="text"
                  name="codes_number"
                  mask={/^[0-9]{0,7}$/}
                  id="request-codes-number"
                  className={classnames({ invalid: errors.codes_number && touched.codes_number })}
                  validate={validateCodesNumber}
                  component={MaskedInput}
                />
              </label>

              <label htmlFor="request-sku" className="required">
                <LocalizedText id="qau-request-codes-sku" tagName="span" />
                <Field
                  type="text"
                  name="sku"
                  id="request-sku"
                  validate={validateText}
                  className={classnames({ invalid: errors.sku && touched.sku })}
                  maxLength="20"
                />
              </label>

              <label htmlFor="request-brand-id-input" className="required">
                <LocalizedText id="qau-request-codes-brand-id" tagName="span" />
                <Field
                  name="brand_id"
                  id="request-brand-id"
                  optionsList={brandIdList}
                  component={EditableSelect}
                  className={classnames({ 'read-only': hasSingleBrandId })}
                  maxLength="64"
                  preselectFirst
                  editDisabled
                />
              </label>
            </div>
            <AutoValidateForm onValidated={onFormValidated} />
          </Form>
        )}
      </Formik>
    </div>
  );
};

RequestCodesForm.propTypes = {
  brandIdList: PropTypes.arrayOf(PropTypes.string).isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  setFormValid: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
};

export default connect(null, { setFooterConfig })(RequestCodesForm);
